import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Alert, Typography, MenuItem, TextField, Switch, FormControlLabel,
  Chip, CircularProgress,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SaveIcon from '@mui/icons-material/Save';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { SuperAdminDataTable, AdminColumn, apiErrorMessage } from '../../components/superadmin/SuperAdminDataTable';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, brandCss } from '../../theme/appTheme';
import { normalizeImportRows } from '../../utils/exportToExcel';

interface TenantModuleRow extends Record<string, unknown> {
  moduleKey: string;
  label: string;
  group: string;
  type: string;
  enabled: boolean;
  locked: boolean;
  lockReason?: string;
}

const IMPORT_COLUMNS = [
  { field: 'moduleKey', header: 'Module Key' },
  { field: 'enabled', header: 'Enabled' },
];

function parseEnabled(value: unknown): boolean {
  const v = String(value ?? '').trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === '1' || v === 'enabled' || v === 'on';
}

export const TenantModuleAccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState<any[]>([]);
  const [tenantId, setTenantId] = useState(searchParams.get('tenant') || '');
  const [companyName, setCompanyName] = useState('');
  const [planName, setPlanName] = useState('');
  const [modules, setModules] = useState<TenantModuleRow[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingModules, setLoadingModules] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    superAdminAPI.getTenants()
      .then((r) => setTenants(r.data.filter((t: any) => t.status === 'active')))
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load tenants')))
      .finally(() => setLoadingTenants(false));
  }, []);

  const loadModules = async (id: string) => {
    if (!id) return;
    setLoadingModules(true);
    setError('');
    try {
      const res = await superAdminAPI.getTenantModuleAccess(id);
      setCompanyName(res.data.companyName);
      setPlanName(res.data.planName);
      setModules(res.data.modules || []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load module access'));
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  useEffect(() => {
    if (tenantId) loadModules(tenantId);
  }, [tenantId]);

  const toggleModule = (moduleKey: string, enabled: boolean) => {
    setModules((prev) => prev.map((m) =>
      m.moduleKey === moduleKey ? { ...m, enabled } : m
    ));
  };

  const enableAll = () => {
    setModules((prev) => prev.map((m) => (m.locked ? m : { ...m, enabled: true })));
  };

  const disableAll = () => {
    setModules((prev) => prev.map((m) => (m.locked ? m : { ...m, enabled: false })));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError('');
    try {
      const res = await superAdminAPI.updateTenantModuleAccess(
        tenantId,
        modules.map((m) => ({ moduleKey: m.moduleKey, enabled: m.enabled }))
      );
      setModules(res.data.modules || []);
      setMessage(`Module access saved for ${res.data.companyName}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleImport = useCallback((records: Record<string, unknown>[]) => {
    const normalized = normalizeImportRows(records, IMPORT_COLUMNS);
    if (!normalized.length) return;
    setModules((prev) => prev.map((mod) => {
      if (mod.locked) return mod;
      const match = normalized.find((row) => {
        const key = String(row.moduleKey || row['Module Key'] || '').trim();
        return key === mod.moduleKey;
      });
      if (!match) return mod;
      return { ...mod, enabled: parseEnabled(match.enabled ?? match['Enabled']) };
    }));
    setMessage(`Imported ${normalized.length} row(s) — review and save`);
  }, []);

  const columns: AdminColumn<TenantModuleRow>[] = useMemo(() => [
    {
      id: 'label',
      header: 'Module',
      searchValue: (row) => `${row.label} ${row.moduleKey}`,
      exportValue: (row) => row.label,
      render: (row) => (
        <Box>
          <Typography fontWeight={700} fontSize={13}>{row.label}</Typography>
          <Typography fontSize={11} color="text.secondary">{row.moduleKey}</Typography>
        </Box>
      ),
    },
    {
      id: 'group',
      header: 'Group',
      exportValue: (row) => row.group,
      filterType: 'select',
      filterOptions: Array.from(new Set(modules.map((m) => m.group))).sort().map((g) => ({ label: g, value: g })),
      filterValue: (row) => row.group,
      render: (row) => <Typography fontSize={12}>{row.group}</Typography>,
    },
    {
      id: 'type',
      header: 'Type',
      exportValue: (row) => row.type,
      filterType: 'select',
      filterOptions: [{ label: 'Core', value: 'core' }, { label: 'CRM', value: 'custom' }],
      filterValue: (row) => row.type,
      render: (row) => (
        <Chip
          label={row.type === 'custom' ? 'CRM' : 'Core'}
          size="small"
          sx={{ fontSize: 10.5, height: 22, fontWeight: 700 }}
        />
      ),
    },
    {
      id: 'enabled',
      header: 'Enabled',
      exportValue: (row) => (row.enabled ? 'Yes' : 'No'),
      filterType: 'select',
      filterOptions: [{ label: 'Enabled', value: 'true' }, { label: 'Disabled', value: 'false' }],
      filterValue: (row) => String(row.enabled),
      render: (row) => (
        <Chip
          label={row.enabled ? 'On' : 'Off'}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: 10.5,
            bgcolor: row.enabled ? brandCss.green100 : '#f1f5f9',
            color: row.enabled ? brandCss.primaryDark : brandColors.textMuted,
          }}
        />
      ),
    },
    {
      id: 'lockReason',
      header: 'Notes',
      searchValue: (row) => row.lockReason || '',
      exportValue: (row) => row.lockReason || '',
      render: (row) => (
        <Typography fontSize={11} color={row.locked ? 'error' : 'text.secondary'}>
          {row.locked ? row.lockReason : '—'}
        </Typography>
      ),
    },
  ], [modules]);

  const exportExtraColumns = useMemo(() => [
    { id: 'moduleKey', header: 'Module Key', exportValue: (row: TenantModuleRow) => row.moduleKey },
  ], []);

  const enabledCount = modules.filter((m) => m.enabled).length;

  return (
    <Box>
      <PageHeader
        title="Tenant Module Access"
        subtitle={getPageSubtitle('tenant_module_access')}
        icon={<ViewModuleIcon />}

        action={(
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" onClick={enableAll} disabled={!tenantId || loadingModules}>Enable all</Button>
            <Button size="small" onClick={disableAll} disabled={!tenantId || loadingModules}>Disable all</Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!tenantId || saving || loadingModules}
              sx={catchyPrimaryButtonSx}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        )}
      />

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          label="Tenant"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          disabled={loadingTenants}
          sx={{ ...catchyTextFieldSx, minWidth: 280 }}
          size="small"
        >
          <MenuItem value="">— Select tenant —</MenuItem>
          {tenants.map((t) => (
            <MenuItem key={t._id} value={t._id}>{t.companyName}</MenuItem>
          ))}
        </TextField>
        {tenantId && !loadingModules && (
          <>
            <Chip label={companyName} size="small" color="primary" variant="outlined" />
            <Chip label={`Plan: ${planName}`} size="small" />
            <Chip label={`${enabledCount} / ${modules.length} on`} size="small" sx={{ bgcolor: brandCss.green100 }} />
          </>
        )}
      </Box>

      {!tenantId && (
        <Typography color="text.secondary" fontSize={14} sx={{ py: 4, textAlign: 'center' }}>
          Select a tenant to manage module access. Only Dashboard and Settings are always on.
        </Typography>
      )}

      {tenantId && loadingModules && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="success" />
        </Box>
      )}

      {tenantId && !loadingModules && (
        <SuperAdminDataTable<TenantModuleRow>
          columns={columns}
          rows={modules}
          exportExtraColumns={exportExtraColumns}
          exportFileName={`tenant-modules-${companyName || tenantId}`}
          exportSheetName="Modules"
          enableImport
          onImport={handleImport}
          searchPlaceholder="Search modules..."
          statusField=""
          statusOptions={[{ label: 'All', value: 'all' }]}
          getRowId={(row) => row.moduleKey}
          getStatus={(row) => String(row.enabled)}
          emptyMessage="No assignable modules"
          recordLabel="modules"
          renderActions={(row) => (
            <FormControlLabel
              control={
                <Switch
                  checked={row.enabled}
                  disabled={row.locked}
                  onChange={(e) => toggleModule(row.moduleKey, e.target.checked)}
                  color="success"
                  size="small"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          )}
        />
      )}
    </Box>
  );
};
